import * as R from "ramda";
import { Subject } from "rxjs";
import { Button, Col, Form, Input, Modal, Row, Select } from "antd";
import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";

import { isFunction } from "@/util";
import { TooltipIcon } from "./dropdown-select-upload-type";
import { checkMediaIsExist } from "@/containers/Video/aliyun-service-api";
import { beforeUploadCheckFail, UploadResourceFile } from "./upload-resource";
import { hasDecoratePlusAuth } from "@/components/super-administrator-setting";

const layout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 16 },
};

export const acceptMap = {
  video: "video/mp4,video/mov,video/flv,video/m4v,video/mkv",
  audio: "audio/mp3,audio/wma,audio/wav,audio/amr,audio/m4a",
  image: "image/jpeg,image/png,image/bmp,image/gif,image/webp",
  //  因为字幕文件格式太多，所以不做限制, 在beforeUpload中做限制
  text: "*/*",
};

export const AcceptTextTypes = [
  "srt",
  "ass",
  "ssa",
  "lrc",
  "txt",
  "word",
  "pdf",
  "ppt",
];

const subject = new Subject();
export const getBaseNameOfFile = (name = "") => {
  const arr = name.split(".");
  if (arr.length > 1) {
    return arr.slice(0, -1).join(".");
  }
  return name;
};

export const getSingleFileListByUrl = (url) => {
  if (!url) {
    return [];
  }
  return [{ url, uid: "-1", name: "封面" }];
};

export const getTypeName = (type) => {
  switch (type) {
    case "video":
      return "视频";
    case "audio":
      return "音频";
    case "image":
      return "图片";
    case "text":
      return "字幕";
    default:
      return "资源";
  }
};

export const useTypeName = ({ type }) => {
  return useMemo(() => {
    return getTypeName(type);
  }, [type]);
};

export const uploadResourceVideoLimit = "1GB";

const UploadResourceModal = (props) => {
  const {
    getFieldDecorator,
    validateFields,
    setFieldsValue,
    getFieldsValue,
    getFieldValue,
  } = props.form;
  const { children } = props;
  const tagList = R.pathOr([], ["tagList"])(props);
  const [modalOpen, setModalOpen] = useState(false);
  const [type, setType] = useState("video");
  const typeName = useTypeName({ type });

  const RESOLVE = useRef();
  const REJECT = useRef();

  const [accept, setAccept] = useState("*/*");

  useEffect(() => {
    setAccept(acceptMap[type] || "*/*");
  }, [type]);

  const open = ({ type: openType }) => {
    setType(openType);
    setModalOpen(true);
    setFieldsValue({ hidden: 1 });
    return new Promise((resolve, reject) => {
      RESOLVE.current = resolve;
      REJECT.current = reject;
    });
  };

  const isVideo = R.equals(type, "video");

  const [isUploadFileForVideo, setIsUploadFileForVideo] = useState(true);

  return (
    <Fragment>
      <Modal
        destroyOnClose
        width={640}
        title={"上传" + typeName}
        visible={modalOpen}
        maskClosable={false}
        onCancel={() => {
          setModalOpen(false);
          const values = getFieldsValue();
          REJECT.current && REJECT.current(values);
          subject.next(values);
        }}
        onOk={() => {
          //  validate form
          validateFields((err, values) => {
            if (err) return;
            const optionCoverUrl = R.pathOr(
              "",
              ["options", "coverUrl", "fileList", 0, "url"],
              values
            );
            const newValue = R.assocPath(
              ["options", "coverUrl"],
              optionCoverUrl,
              values
            );
            RESOLVE.current && RESOLVE.current(newValue);
            setModalOpen(false);
          });
        }}
      >
        <Form labelCol={layout.labelCol} wrapperCol={layout.wrapperCol}>
          <Form.Item label={`${typeName}名称`} name="name">
            {getFieldDecorator("name", {
              rules: [
                {
                  required: true,
                  message: `请输入${typeName}名称`,
                },
              ],
            })(<Input placeholder="当资源上传后会自动获取文件名" />)}
          </Form.Item>
          <Form.Item label={`上传到标签`} name="tag">
            {getFieldDecorator("tag", {
              // rules: [{ required: true }],
            })(
              <Select>
                {tagList.map((tag) => (
                  <Select.Option key={tag.id} value={tag.id}>
                    {tag.name}
                  </Select.Option>
                ))}
              </Select>
            )}
          </Form.Item>
          {isVideo && (
            <Form.Item
              label={
                <span>
                  {typeName}封面
                  <TooltipIcon title="默认视频第三帧为封面，可人工修改" />
                </span>
              }
            >
              {getFieldDecorator("options.coverUrl")(
                <UploadResourceFile {...props} type="image" />
              )}
            </Form.Item>
          )}
          <Form.Item label={`${typeName}状态`} name="status">
            {getFieldDecorator("hidden", {
              rules: [
                {
                  required: true,
                  message: `请选择${typeName}状态`,
                },
              ],
            })(
              <Select defaultActiveFirstOption>
                <Select.Option value={1}>隐藏</Select.Option>
                <Select.Option value={0}>公开</Select.Option>
              </Select>
            )}
          </Form.Item>
          {type === "video" ? (
            <Form.Item label={`${typeName}说明`} name="description">
              {getFieldDecorator("desc")(<Input.TextArea />)}
            </Form.Item>
          ) : null}
          {isVideo ? (
            <Row
              style={{
                marginBottom: 24,
                display: hasDecoratePlusAuth() ? "inherit" : "none",
              }}
            >
              <Col
                push={layout.labelCol.span}
                span={layout.wrapperCol.span}
                className="color-gray"
              >
                <Button.Group>
                  <Button
                    type={isUploadFileForVideo ? "primary" : "default"}
                    onClick={() => {
                      setIsUploadFileForVideo(true);
                    }}
                  >
                    上传文件
                  </Button>
                  <Button
                    type={isUploadFileForVideo ? "default" : "primary"}
                    onClick={() => {
                      setIsUploadFileForVideo(false);
                    }}
                  >
                    输入链接
                  </Button>
                </Button.Group>
              </Col>
            </Row>
          ) : null}

          {isVideo && !isUploadFileForVideo ? (
            <Form.Item label={`输入${typeName}链接`} name="videoUrl" required>
              {getFieldDecorator("videoUrl", {
                rules: [
                  {
                    validator: (rule, value, callback) => {
                      const message = "视频链接无效";
                      if (!value) {
                        callback(message);
                        return;
                      }
                      checkMediaIsExist(value).then(
                        (res) => {
                          if (R.pathEq(true, ["is_exist"], res)) {
                            callback();
                          } else {
                            callback(message);
                          }
                        },
                        () => {
                          callback(message);
                        }
                      );
                    },
                  },
                ],
              })(<Input placeholder="请输入视频链接" />)}
            </Form.Item>
          ) : (
            <Form.Item
              label={`上传${typeName}`}
              name="file"
              className="resource-upload-container"
            >
              {getFieldDecorator("file", {
                rules: [
                  {
                    required: true,
                    message: `请上传${typeName}`,
                  },
                  {
                    validator: (rule, value, callback) => {
                      const valueStatusIsCheckFail = R.pathEq(
                        "done",
                        ["fileList", 0, "status"],
                        beforeUploadCheckFail
                      );
                      if (valueStatusIsCheckFail) {
                        callback(`文件不符合要求`);
                      } else {
                        callback();
                      }
                    },
                  },
                ],
              })(
                <UploadResourceFile
                  className="w-full"
                  accept={accept}
                  type={type}
                  {...props}
                  afterDone={(file) => {
                    const name = file.name;
                    setFieldsValue({ name: getBaseNameOfFile(name) });
                    const coverUrl = getFieldValue("options.coverUrl");
                    if (isVideo && !coverUrl) {
                      const snapshotUrl = `${file.url}?x-oss-process=video/snapshot,t_3000,f_jpg,m_fast,ar_auto`;
                      const fileList = [
                        { url: snapshotUrl, uid: "-1", name: "封面" },
                      ];
                      setFieldsValue({ "options.coverUrl": { fileList } });
                    }
                  }}
                />
              )}
            </Form.Item>
          )}
        </Form>

        <div className="mt-4 opacity-50">
          <Row>
            <Col
              push={layout.labelCol.span}
              span={layout.wrapperCol.span}
              className="color-gray"
            >
              {(() => {
                switch (type) {
                  case "video":
                    return (
                      <Fragment>
                        <p>
                          支持mp4，mov，flv，mkv格式；文件小于
                          {uploadResourceVideoLimit}
                          ，上传后自动转化为mp4（编码格式 H264）
                        </p>
                      </Fragment>
                    );
                  case "audio":
                    return (
                      <Fragment>
                        <p>
                          支持mp3、 wma、 wav、 amr、
                          m4a，文件大小不超过1G。上传后音频将自动进行转码
                        </p>
                      </Fragment>
                    );
                  case "image":
                    return (
                      <Fragment>
                        <p>
                          支持jpg、jpeg、png、bmp、gif、webp格式，文件大小不超过30M
                        </p>
                      </Fragment>
                    );
                  case "text":
                    return (
                      <Fragment>
                        <p>
                          支持SRT、ASS/SSA、 LRC、 TXT、 word、 pdf、 ppt，
                          文件大小不超过1G。上传后文件将自动转码
                        </p>
                      </Fragment>
                    );
                  default:
                    return null;
                }
              })()}
            </Col>
          </Row>
        </div>
      </Modal>
      {isFunction(children)
        ? children({ open, form: props.form, subject })
        : null}
    </Fragment>
  );
};

export const UploadResourceModalForm = Form.create()(UploadResourceModal);


// WEBPACK FOOTER //
// ./src/containers/Video/widgets/upload-resource-modal.js